import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { FaFileExcel, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "sheetjs-style";
import { saveAs } from "file-saver";
import "../modal/modal.css";
import "./listaTabelaModal.css";

/**
 * Modal reutilizável para listagens em tabela com exportação Excel.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {string} [props.countLabel] — ex.: "Pendentes"
 * @param {Array<{ key: string, label: string, excelLabel?: string, render?: Function, excelValue?: Function, excelEmptyValue?: string, excelEmptyStyle?: object, excelStyle?: Function, className?: string }>} props.columns
 * @param {Array<object>} props.rows
 * @param {boolean} [props.loading]
 * @param {string|null} [props.error]
 * @param {string} [props.emptyMessage]
 * @param {string} [props.loadingMessage]
 * @param {string} props.excelFileName — sem extensão
 * @param {string} [props.excelSheetName]
 * @param {(row: object) => string|null|undefined} [props.getRowHref]
 * @param {string} [props.size] — lg | xl
 * @param {string} [props.dialogClassName] — classes extras no dialog do Modal
 * @param {React.ReactNode} [props.toolbarExtra]
 */

const ESTILO_HEADER_EXCEL = {
  font: { bold: true, color: { rgb: "0F172A" } },
  fill: { patternType: "solid", fgColor: { rgb: "F1F5F9" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
};

function resolverCelulaExcel(col, row) {
  let bruto;
  if (typeof col.excelValue === "function") {
    bruto = col.excelValue(row);
  } else {
    bruto = row?.[col.key];
  }

  const estavaVazio = bruto == null || bruto === "";
  const valor = estavaVazio
    ? col.excelEmptyValue != null
      ? col.excelEmptyValue
      : ""
    : bruto;

  let estilo = null;
  if (estavaVazio && col.excelEmptyStyle) {
    estilo = col.excelEmptyStyle;
  } else if (typeof col.excelStyle === "function") {
    estilo = col.excelStyle(valor, row) || null;
  }

  return { valor, estilo };
}

export default function ListaTabelaModal({
  show,
  onClose,
  title,
  subtitle,
  countLabel = "Registros",
  columns = [],
  rows = [],
  loading = false,
  error = null,
  emptyMessage = "Nenhum registro encontrado.",
  loadingMessage = "Carregando…",
  excelFileName = "lista",
  excelSheetName = "Dados",
  getRowHref,
  size = "lg",
  dialogClassName = "",
  toolbarExtra = null,
}) {
  const [exportando, setExportando] = useState(false);

  const total = rows.length;
  const podeExportar = !loading && !error && total > 0;

  const colunasExcel = useMemo(
    () =>
      columns.map((col) => ({
        header: col.excelLabel || col.label,
        key: col.key,
        excelValue: col.excelValue,
        excelEmptyValue: col.excelEmptyValue,
        excelEmptyStyle: col.excelEmptyStyle,
        excelStyle: col.excelStyle,
      })),
    [columns],
  );

  const handleExportarExcel = useCallback(() => {
    if (!podeExportar || exportando) {
      return;
    }

    setExportando(true);
    try {
      const headers = colunasExcel.map((col) => col.header);
      const dataRows = rows.map((row) =>
        colunasExcel.map((col) => resolverCelulaExcel(col, row).valor),
      );

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
      ws["!cols"] = headers.map((header) => ({
        wch: Math.min(Math.max(String(header).length + 4, 18), 42),
      }));

      for (let c = 0; c < colunasExcel.length; c += 1) {
        const headerCell = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[headerCell]) {
          ws[headerCell].s = ESTILO_HEADER_EXCEL;
        }
      }

      for (let r = 0; r < rows.length; r += 1) {
        for (let c = 0; c < colunasExcel.length; c += 1) {
          const col = colunasExcel[c];
          const cellRef = XLSX.utils.encode_cell({ r: r + 1, c });
          const cell = ws[cellRef];
          if (!cell) continue;

          const { estilo } = resolverCelulaExcel(col, rows[r]);
          if (estilo) {
            cell.s = estilo;
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, excelSheetName.slice(0, 31));

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        bookSST: false,
        cellStyles: true,
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const safeName = String(excelFileName || "lista").replace(/\.xlsx$/i, "");
      saveAs(blob, `${safeName}.xlsx`);
      toast.success("Excel baixado com sucesso.");
    } catch (err) {
      console.error("Erro ao exportar Excel da listagem:", err);
      toast.error("Não foi possível gerar o arquivo Excel.");
    } finally {
      setExportando(false);
    }
  }, [
    colunasExcel,
    excelFileName,
    excelSheetName,
    exportando,
    podeExportar,
    rows,
  ]);

  const renderCelula = (col, row) => {
    if (typeof col.render === "function") {
      return col.render(row);
    }
    const valor = row?.[col.key];
    if (valor == null || valor === "") {
      return <span className="ltm-muted">—</span>;
    }
    return valor;
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={size}
      centered
      scrollable
      dialogClassName={["ltm-dialog", dialogClassName].filter(Boolean).join(" ")}
      contentClassName="ltm-content"
    >
      <Modal.Header closeButton className="ltm-header">
        <Modal.Title className="titulo-modal">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ltm-body">
        {(subtitle || !loading) && (
          <div className="ltm-meta">
            {subtitle ? <p className="ltm-subtitle">{subtitle}</p> : null}
            {!loading && !error ? (
              <p className="ltm-count">
                {countLabel}: <strong>{total.toLocaleString("pt-BR")}</strong>
              </p>
            ) : null}
            {toolbarExtra}
          </div>
        )}

        {loading ? (
          <div className="apl-loading ltm-loading" aria-live="polite">
            <FaSpinner className="fa-spin" size={22} aria-hidden />
            <p>{loadingMessage}</p>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="ltm-error" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error && total === 0 ? (
          <p className="ltm-empty">{emptyMessage}</p>
        ) : null}

        {!loading && !error && total > 0 ? (
          <div className="ltm-table-wrap">
            <table className="ltm-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className={col.className}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const href =
                    typeof getRowHref === "function" ? getRowHref(row) : null;
                  const rowKey = row?.id ?? `row-${index}`;

                  return (
                    <tr key={rowKey}>
                      {columns.map((col, colIndex) => {
                        const content = renderCelula(col, row);
                        const isFirst = colIndex === 0;

                        return (
                          <td
                            key={`${rowKey}-${col.key}`}
                            className={col.className}
                          >
                            {isFirst && href ? (
                              <Link
                                to={href}
                                className="ltm-row-link"
                                onClick={onClose}
                                title="Abrir cadastro"
                              >
                                {content}
                              </Link>
                            ) : (
                              content
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="ltm-footer">
        <button
          type="button"
          className="apl-btn apl-btn--ghost ltm-export-btn"
          onClick={handleExportarExcel}
          disabled={!podeExportar || exportando}
          title={
            podeExportar
              ? "Baixar planilha Excel"
              : "Nenhum dado para exportar"
          }
        >
          {exportando ? (
            <FaSpinner className="fa-spin" size={14} aria-hidden />
          ) : (
            <FaFileExcel size={14} aria-hidden />
          )}
          {exportando ? "Gerando…" : "Baixar Excel"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
